import React, { useEffect, useRef, useState } from "react";
import { Loader2, Zap, Lock } from "lucide-react";
import { api, fmtUSD, fmtINR } from "@/lib/api";
import { useRate } from "@/context/RateContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const useCost = (modelId, settings, enabled = true) => {
  const [usd, setUsd] = useState(null);
  const seq = useRef(0);
  const key = JSON.stringify(settings);
  useEffect(() => {
    if (!modelId || !enabled) { setUsd(null); return; }
    const t = setTimeout(() => {
      const id = ++seq.current;
      api.post("/models/cost", { model_id: modelId, settings })
        .then((r) => { if (id === seq.current) setUsd(r.data.usd); })
        .catch(() => { if (id === seq.current) setUsd(null); });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, key, enabled]);
  return usd;
};

export const Money = ({ usd, className = "" }) => {
  const { rate } = useRate();
  return <span className={className}>{fmtINR(usd * rate)} · {fmtUSD(usd)}</span>;
};

const Inner = ({ busy, disabledReason, label, usd, rate, testid }) => (
  <>
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : disabledReason ? <Lock className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
    <span>{busy ? "Starting…" : label}</span>
    {usd != null && !busy && (
      <span className="ml-1 flex items-baseline gap-1.5" data-testid={`${testid}-cost`}>
        <span key={fmtINR(usd * rate)} className="ax-inr-pop text-[15px] font-extrabold px-2 py-0.5 rounded-md bg-black/80 text-[#9DFF7F]" data-testid={`${testid}-inr`}>{fmtINR(usd * rate)}</span>
        <span className="text-[10px] font-mono opacity-70" data-testid={`${testid}-usd`}>{fmtUSD(usd)}</span>
      </span>
    )}
  </>
);

// confirm: show a small "are you sure" popup before running (used on video generations).
export default function GenerateButton({ usd, onClick, busy, disabledReason, testid = "generate-button", className = "", label = "Generate", confirm = false, confirmNote }) {
  const { rate } = useRate();
  const [ask, setAsk] = useState(false);
  const disabled = busy || !!disabledReason;
  const btn = (
    <button onClick={confirm ? () => setAsk(true) : onClick} disabled={disabled} data-testid={testid} title={disabledReason || ""} className={`ax-generate ${className}`}>
      <Inner busy={busy} disabledReason={disabledReason} label={label} usd={usd} rate={rate} testid={testid} />
    </button>
  );
  if (!confirm) return btn;
  return (
    <Popover open={ask && !disabled} onOpenChange={setAsk}>
      <PopoverTrigger asChild>{btn}</PopoverTrigger>
      <PopoverContent side="top" className={`w-72 p-3 border-zinc-800 text-zinc-100 ${className.includes("bfx") ? "bg-[#070B14] border-[#1065c0]/50 bfx" : "bg-[#0E1114]"}`} data-testid={`${testid}-confirm`}>
        <div className="text-sm font-semibold text-white">Start this video generation?</div>
        <div className="text-[11px] text-zinc-400 mt-1">{confirmNote || "Credits are used as soon as it starts."}</div>
        {usd != null && <div className="text-xs mt-2" data-testid={`${testid}-confirm-cost`}>Cost <Money usd={usd} className="font-mono text-white" /></div>}
        <div className="flex gap-2 mt-3">
          <button type="button" onClick={() => setAsk(false)} data-testid={`${testid}-confirm-cancel`} className="ax-chip flex-1 justify-center">Cancel</button>
          <button type="button" onClick={() => { setAsk(false); onClick?.(); }} data-testid={`${testid}-confirm-ok`} className={`ax-generate !h-[34px] !px-3 !text-xs flex-1 ${className.includes("bfx") ? "bfx-generate" : ""}`}>
            <Zap className="w-3.5 h-3.5" />Confirm</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
